import { useEffect, useRef, useState } from "react";
import { ArrowDown, ArrowUp, ChevronRight, Forward, Loader2, Paperclip, Phone, RefreshCw, Reply, UserPlus, Users, ExternalLink, FolderOpen, Info, Mail, MessageSquare } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Modal } from "@/components/common";
import MailSchreibenModal from "@/components/MailSchreibenModal";
import MailKundenSuche, { kundenLaden } from "@/components/MailKundenSuche";
import AuswahlFeld from "@/components/AuswahlFeld";
import { MAIL_ROLLEN_ERSATZ } from "@/components/MailAdressenFeld";
import MailAnhaengeBereich from "./MailAnhaengeBereich";

/**
 * MailZentraleListe (A-010 M3, 16.10.2026) — Reiter „Posteingang“ / „Gesendet“ der Mail-Abteilung.
 * Zweispaltig wie der Mailverlauf: Liste links, Detail mit Kundenblick rechts. Daten aus dem Mail-Speicher
 * über alle Postfächer (/module-mail-inbox/zentrale/*), „Neu laden“ holt nur neue Mails.
 * Props: { richtung: "ein" | "aus", konten: [{id, label}], onGeladen?() }
 * M3b: Portal-Mails (Benachrichtigungen der Suite) mit Kunde/Portal/Projekt und „Im Portal antworten“ / „Mail an Kunden“;
 * Schalter „Systemmails zeigen“ (gemerkt); Hinweise mit Grund statt stiller Leere (Backend: „fuehrung“).
 */
const SEITE = 50;
const SYSTEM_KEY = "mailZentrale.systemmailsZeigen";
const merkLesen = () => { try { return localStorage.getItem(SYSTEM_KEY) === "1"; } catch { return false; } };
const merkSchreiben = (v) => { try { localStorage.setItem(SYSTEM_KEY, v ? "1" : "0"); } catch { /* noop */ } };
const datumKurz = (d) => {
  const t = new Date(d || "");
  return isNaN(t) ? (d || "").slice(0, 16).replace("T", " ") : t.toLocaleString("de-DE", { day: "2-digit", month: "2-digit", year: "2-digit", hour: "2-digit", minute: "2-digit" });
};
const mailKey = (m) => `${m.account_id}/${m.folder}/${m.uid}`;

// Name aus „Max Mustermann“ bzw. Adresse grob zerlegen (nur Vorschlag zum Anlegen)
const nameVorschlag = (name, adresse) => {
  const n = (name || "").replace(/["']/g, "").trim();
  if (n && !n.includes("@")) {
    const teile = n.split(/\s+/);
    return { vorname: teile.length > 1 ? teile.slice(0, -1).join(" ") : "", nachname: teile[teile.length - 1] };
  }
  const lokal = (adresse || "").split("@")[0].replace(/[._-]+/g, " ").trim();
  const teile = lokal.split(/\s+/).map((x) => x.charAt(0).toUpperCase() + x.slice(1));
  return { vorname: teile.length > 1 ? teile[0] : "", nachname: teile.length > 1 ? teile.slice(1).join(" ") : (teile[0] || "") };
};

const MailZentraleListe = ({ richtung = "ein", konten = [], onGeladen }) => {
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const [qAktiv, setQAktiv] = useState("");
  const [konto, setKonto] = useState("");
  const [ohneKunde, setOhneKunde] = useState(false);
  const [systemZeigen, setSystemZeigen] = useState(merkLesen);
  const [ausgeblendet, setAusgeblendet] = useState(0);
  const [items, setItems] = useState([]);
  const [mehr, setMehr] = useState(false);
  const [seite, setSeite] = useState(0);
  const [laden, setLaden] = useState(false);
  const [abgleich, setAbgleich] = useState(false);
  const [sel, setSel] = useState("");
  const [detail, setDetail] = useState(null);
  const [detailLaden, setDetailLaden] = useState(false);
  const [schreiben, setSchreiben] = useState(null); // { weiterleiten } bzw. { anKunden } (M3b)
  const [anlegen, setAnlegen] = useState(null);     // Formular „Als Kunde anlegen“
  const [dupl, setDupl] = useState(null);           // 409: mögliche Doppel-Kunden
  const [zuordnen, setZuordnen] = useState(false);
  const [adrFrage, setAdrFrage] = useState(null);  // A-018 Teil 2: „Adresse beim Kunden speichern?“ {adresse, kunde_id, kunde_name, rolle}
  const [busy, setBusy] = useState(false);
  const abgleichGelaufen = useRef(false);

  // Suche mit kurzer Verzögerung
  useEffect(() => {
    const t = setTimeout(() => setQAktiv(q.trim()), 400);
    return () => clearTimeout(t);
  }, [q]);

  const ladeListe = async (s = 0, anhaengen = false) => {
    setLaden(true);
    try {
      const r = await api.get("/module-mail-inbox/zentrale/mails", {
        params: { richtung, q: qAktiv || undefined, account_id: konto || undefined, ohne_kunde: ohneKunde || undefined,
          systemmails: (richtung === "ein" && systemZeigen) || undefined, seite: s, limit: SEITE },
      });
      const neu = r.data?.items || [];
      if (s === 0) setAusgeblendet(r.data?.system_ausgeblendet || 0);
      setItems((alt) => (anhaengen ? [...alt, ...neu.filter((x) => !alt.some((y) => mailKey(y) === mailKey(x)))] : neu));
      setMehr(!!r.data?.mehr);
      setSeite(s);
      return neu;
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Mails laden fehlgeschlagen");
      return [];
    } finally {
      setLaden(false);
    }
  };

  const neuLaden = async (still = false) => {
    setAbgleich(true);
    try {
      const r = await api.post("/module-mail-inbox/zentrale/abgleich");
      const d = r.data || {};
      if (!still || d.neu) {
        const teile = [d.laeuft_schon ? "Abgleich läuft bereits" : `${d.neu} neue Mail(s) geholt`];
        if (!d.vollstaendig && !d.laeuft_schon) teile.push("noch nicht alles – beim nächsten „Neu laden“ geht es weiter");
        if ((d.nicht_erreichbar || []).length) teile.push(`nicht erreichbar: ${d.nicht_erreichbar.join(", ")}`);
        (d.nicht_erreichbar || []).length ? toast.warning(teile.join(" · ")) : toast.success(teile.join(" · "));
      }
      await ladeListe(0);
      onGeladen?.();
    } catch (err) {
      if (!still) toast.error(err?.response?.data?.detail || "Neu laden fehlgeschlagen");
    } finally {
      setAbgleich(false);
    }
  };

  useEffect(() => { ladeListe(0); }, [richtung, qAktiv, konto, ohneKunde, systemZeigen]);  // eslint-disable-line
  const systemUmschalten = (v) => { merkSchreiben(v); setSystemZeigen(v); };
  // Beim ersten Öffnen einmal still abgleichen (wie der Mailverlauf), Liste erscheint vorher schon aus dem Speicher
  useEffect(() => {
    if (abgleichGelaufen.current) return;
    abgleichGelaufen.current = true;
    neuLaden(true);
  }, []);  // eslint-disable-line

  const oeffnen = async (m) => {
    setSel(mailKey(m));
    setZuordnen(false);
    setAdrFrage(null);
    setDetail(null);
    setDetailLaden(true);
    try {
      const r = await api.post("/module-mail-inbox/zentrale/mail", { account_id: m.account_id, folder: m.folder, uid: m.uid });
      setDetail(r.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Mail laden fehlgeschlagen");
    } finally {
      setDetailLaden(false);
    }
  };

  const detailNeu = async () => {
    if (!detail) return;
    const meta = { account_id: detail.account_id, folder: detail.folder, uid: detail.uid };
    const r = await api.post("/module-mail-inbox/zentrale/mail", meta);
    setDetail(r.data);
    // Kennzeichen in der Liste nachziehen, ohne die Liste neu zu laden
    setItems((alt) => alt.map((x) => (x.kontakt_adresse && x.kontakt_adresse === r.data.kontakt_adresse) || mailKey(x) === mailKey(meta)
      ? { ...x, kunde: r.data.kunde ? { id: r.data.kunde.id, name: r.data.kunde.name, quelle: r.data.kunde.quelle, gefunden_ueber: r.data.kunde.gefunden_ueber } : null, portal: r.data.portal,
          projekte: r.data.projekte.filter((p) => !p.archiv).slice(0, 3), projekte_anzahl: r.data.projekte.length }
      : x));
  };

  const zuordnungSpeichern = async (kunde) => {
    if (!detail?.kontakt_adresse) return;
    setBusy(true);
    try {
      const r = await api.post("/module-mail-inbox/zentrale/zuordnung", { adresse: detail.kontakt_adresse, kunde_id: kunde?.id || "" });
      toast.success(kunde ? `${detail.kontakt_adresse} → ${r.data.kunde_name} (wird gemerkt)` : "Zuordnung gelöst");
      setZuordnen(false);
      // Adresse steht beim Kunden noch nicht: fragen, ob sie dort gespeichert werden soll (dann findet der Mailverlauf sie)
      setAdrFrage(kunde && r.data.adresse_bekannt === false ? { adresse: detail.kontakt_adresse, kunde_id: kunde.id, kunde_name: r.data.kunde_name, rolle: "" } : null);
      await detailNeu();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Zuordnen fehlgeschlagen");
    } finally {
      setBusy(false);
    }
  };

  const adresseSpeichern = async () => {
    if (!adrFrage) return;
    setBusy(true);
    try {
      await api.post("/module-mail-inbox/zentrale/adresse-speichern", { adresse: adrFrage.adresse, kunde_id: adrFrage.kunde_id, rolle: adrFrage.rolle });
      toast.success(`${adrFrage.adresse} steht jetzt bei ${adrFrage.kunde_name}`);
      setAdrFrage(null);
      await detailNeu();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Speichern fehlgeschlagen");
    } finally {
      setBusy(false);
    }
  };

  const anlegenStarten = () => {
    const adr = detail?.kontakt_adresse || "";
    const name = richtung === "aus" ? "" : (detail?.from_email || "").toLowerCase() === adr ? detail?.from_name : "";
    setAnlegen({ anrede: "", firma: "", phone: "", email: adr, ...nameVorschlag(name, adr) });
    setDupl(null);
  };

  const anlegenSenden = async (force = false) => {
    setBusy(true);
    try {
      const r = await api.post("/modules/kunden/data", { ...anlegen, name: [anlegen.vorname, anlegen.nachname].filter(Boolean).join(" "), status: "Anfrage", kontakt_status: "Anfrage", ...(force ? { force: true } : {}) });
      toast.success(`Kunde „${r.data.name}" angelegt`);
      // Adresse merken, falls sie in der Kundenkarte anders geschrieben ist
      if (detail?.kontakt_adresse && (anlegen.email || "").trim().toLowerCase() !== detail.kontakt_adresse) {
        await api.post("/module-mail-inbox/zentrale/zuordnung", { adresse: detail.kontakt_adresse, kunde_id: r.data.id }).catch(() => {});
      }
      kundenLaden(true).catch(() => {});
      try { window.dispatchEvent(new CustomEvent("graupner:data-changed")); } catch { /* noop */ }
      setAnlegen(null);
      setDupl(null);
      await detailNeu();
    } catch (err) {
      if (err?.response?.status === 409) {
        setDupl(err.response.data?.detail?.duplicates || []);
      } else {
        toast.error(err?.response?.data?.detail || "Kunde anlegen fehlgeschlagen");
      }
    } finally {
      setBusy(false);
    }
  };

  const projektOeffnen = (p) => navigate(`/module/projekte/werkbank/${detail.kunde.id}?projekt_id=${p.id}`);
  const aktiveProjekte = (detail?.projekte || []).filter((p) => !p.archiv);
  const fuehrung = detail?.fuehrung || { antworten: true, hinweis: "", anlegen: !!detail?.kontakt_adresse };
  const portal = detail?.portal || null;
  // Projekt fürs Mail-Fenster: das Projekt des Portals, sonst das einzige aktive
  const startProjekt = portal?.projekt_id || (aktiveProjekte.length === 1 ? aktiveProjekte[0].id : "");
  const mailAnKundenGrund = !detail?.kunde ? "Kunde nicht erkannt – erst den Kunden zuordnen oder über die Kundensuche öffnen." : "";
  const imPortalGrund = !portal?.link ? (detail?.kunde ? "Für diesen Kunden ist kein Kundenportal angelegt." : "Kunde nicht erkannt – Portal nicht bestimmbar.") : "";
  const feld = "w-full border border-input rounded-sm px-2 py-1.5 text-sm bg-background";

  return (
    <div className="space-y-3" data-testid={`mail-zentrale-${richtung}`}>
      <div className="flex flex-wrap gap-2 items-center">
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Suche in allen Mails (Absender, Betreff, Text)…"
          className="flex-1 min-w-[200px] border rounded-sm p-2 text-sm bg-background"
          data-testid="zentrale-suche"
        />
        <select value={konto} onChange={(e) => setKonto(e.target.value)} className="border rounded-sm p-2 text-sm bg-background" data-testid="zentrale-postfach" aria-label="Postfach">
          <option value="">Alle Postfächer</option>
          {konten.map((k) => <option key={k.id} value={k.id}>{k.label || k.id}</option>)}
        </select>
        <label className="inline-flex items-center gap-1.5 text-sm border rounded-sm px-2 py-2 cursor-pointer">
          <input type="checkbox" checked={ohneKunde} onChange={(e) => setOhneKunde(e.target.checked)} data-testid="zentrale-ohne-kunde" />
          nur ohne Kunde
        </label>
        {richtung === "ein" && (
          <label className="inline-flex items-center gap-1.5 text-sm border rounded-sm px-2 py-2 cursor-pointer" title="Benachrichtigungen der Suite an sich selbst (Portal-Kontrollkopien, Backup, Registrierung …)">
            <input type="checkbox" checked={systemZeigen} onChange={(e) => systemUmschalten(e.target.checked)} data-testid="zentrale-systemmails" />
            Systemmails zeigen
          </label>
        )}
        <button
          onClick={() => neuLaden(false)}
          disabled={abgleich}
          className="inline-flex items-center gap-1.5 px-3 py-2 text-sm border rounded-sm hover:bg-accent disabled:opacity-50"
          data-testid="zentrale-neu-laden"
          title="Nur neue Mails aus den Postfächern holen (Mail-Speicher)"
        >
          {abgleich ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />} Neu laden
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-3">
        {/* Liste links */}
        <div className="lg:col-span-2 space-y-1 lg:max-h-[70vh] lg:overflow-auto pr-1" data-testid="zentrale-liste">
          {richtung === "ein" && !systemZeigen && ausgeblendet > 0 && (
            <div className="flex items-center justify-between gap-2 text-xs text-muted-foreground border border-dashed rounded-sm px-2 py-1.5" data-testid="zentrale-system-ausgeblendet">
              <span><Info className="w-3 h-3 inline mr-1" />{ausgeblendet} Systemmail{ausgeblendet === 1 ? "" : "s"} ausgeblendet</span>
              <button onClick={() => systemUmschalten(true)} className="underline hover:text-foreground">anzeigen</button>
            </div>
          )}
          {laden && items.length === 0 ? (
            <div className="flex items-center gap-2 text-sm text-muted-foreground py-6 justify-center"><Loader2 className="w-4 h-4 animate-spin" /> Lade…</div>
          ) : items.length === 0 ? (
            <div className="text-sm text-muted-foreground py-6 text-center">
              {abgleich ? "Postfächer werden abgeglichen…" : "Keine Mails gefunden."}
            </div>
          ) : items.map((m) => {
            const k = mailKey(m);
            return (
              <button
                key={k}
                onClick={() => oeffnen(m)}
                className={`w-full text-left border rounded p-2 text-xs hover:bg-accent/60 ${sel === k ? "border-primary bg-primary/5" : ""}`}
                data-testid={`zentrale-row-${m.uid}`}
              >
                <div className="flex items-center gap-2 flex-wrap">
                  {m.direction === "ein" ? (
                    <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-blue-100 text-blue-800 text-[10px]"><ArrowDown className="w-2.5 h-2.5" /> ein</span>
                  ) : (
                    <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-800 text-[10px]"><ArrowUp className="w-2.5 h-2.5" /> aus</span>
                  )}
                  <span className="text-[10px] text-muted-foreground">{m.account_label}</span>
                  {m.anhaenge > 0 && <span className="inline-flex items-center gap-0.5 text-[10px] text-muted-foreground"><Paperclip className="w-2.5 h-2.5" /> {m.anhaenge}</span>}
                  <span className="ml-auto text-[10px] text-muted-foreground">{datumKurz(m.date)}</span>
                </div>
                <div className="font-medium mt-1 truncate">{m.subject || "(kein Betreff)"}</div>
                <div className="text-muted-foreground truncate">
                  {m.direction === "ein" ? `von ${m.from_name ? `${m.from_name} <${m.from_email}>` : m.from_email}` : `an ${m.to_email}`}
                </div>
                <div className="flex flex-wrap gap-1 mt-1">
                  {m.portal && (
                    <span className="px-1.5 py-0.5 rounded bg-violet-100 text-violet-800 dark:bg-violet-900/40 dark:text-violet-200 text-[10px] font-medium" data-testid="zentrale-kennzeichen-portal">Portal</span>
                  )}
                  {m.system?.system && !m.portal && (
                    <span className="px-1.5 py-0.5 rounded bg-muted text-muted-foreground text-[10px] font-medium" data-testid="zentrale-kennzeichen-system">Systemmail</span>
                  )}
                  {m.kunde ? (
                    <>
                      <span className="px-1.5 py-0.5 rounded bg-primary/10 text-primary text-[10px] font-medium" data-testid="zentrale-kennzeichen-kunde">
                        {m.kunde.name}{m.projekte?.[0] ? ` · ${m.projekte[0].titel || "Projekt"}` : ""}{m.projekte_anzahl > 1 ? ` (+${m.projekte_anzahl - 1})` : ""}
                      </span>
                      {m.kunde.gefunden_ueber ? (
                        <span className="px-1.5 py-0.5 rounded bg-amber-50 text-amber-800 dark:bg-amber-900/30 dark:text-amber-200 text-[10px]" data-testid="zentrale-gefunden-ueber">
                          gefunden über: {m.kunde.gefunden_ueber}
                        </span>
                      ) : null}
                    </>
                  ) : m.system && !m.portal ? null : (
                    <span className="px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200 text-[10px] font-medium" data-testid="zentrale-kennzeichen-ohne">
                      {m.portal ? "Kunde nicht erkannt" : "ohne Kunde"}
                    </span>
                  )}
                </div>
              </button>
            );
          })}
          {mehr && (
            <button onClick={() => ladeListe(seite + 1, true)} disabled={laden} className="w-full py-2 text-xs border rounded-sm hover:bg-accent disabled:opacity-50" data-testid="zentrale-mehr">
              {laden ? "Lade…" : `Weitere ${SEITE} laden`}
            </button>
          )}
        </div>

        {/* Detail rechts */}
        <div className="lg:col-span-3 lg:border-l lg:pl-3 lg:max-h-[70vh] lg:overflow-auto" data-testid="zentrale-detail">
          {!sel ? (
            <div className="text-sm text-muted-foreground py-8 text-center flex flex-col items-center gap-2">
              <ChevronRight className="w-5 h-5" /> Wähle eine Mail aus der Liste links.
            </div>
          ) : detailLaden || !detail ? (
            <div className="flex items-center gap-2 text-sm text-muted-foreground py-8 justify-center"><Loader2 className="w-4 h-4 animate-spin" /> Lade Mail…</div>
          ) : (
            <div className="space-y-3 text-sm">
              <div className="flex flex-wrap items-start justify-between gap-2">
                <div className="font-semibold break-words text-base">{detail.subject || "(kein Betreff)"}</div>
                <div className="flex flex-wrap gap-1">
                  {portal ? (
                    <>
                      <button onClick={() => portal.link && navigate(portal.link)} disabled={!portal.link} title={imPortalGrund || `Öffnet ${portal.wo || "das Kundenportal"} dieses Kunden`}
                        className="inline-flex items-center gap-1 px-2.5 py-1 text-xs rounded-sm bg-primary text-primary-foreground hover:opacity-90 disabled:opacity-50" data-testid="btn-zentrale-im-portal">
                        <MessageSquare className="w-3.5 h-3.5" /> Im Portal antworten
                      </button>
                      <button onClick={() => detail.kunde && setSchreiben({ anKunden: true })} disabled={!detail.kunde} title={mailAnKundenGrund || "Neue Mail an die Kundenadresse, ohne Zitat"}
                        className="inline-flex items-center gap-1 px-2.5 py-1 text-xs rounded-sm border hover:bg-accent disabled:opacity-50" data-testid="btn-zentrale-mail-an-kunden">
                        <Mail className="w-3.5 h-3.5" /> Mail an Kunden
                      </button>
                    </>
                  ) : fuehrung.antworten && (
                    <button onClick={() => setSchreiben({ weiterleiten: false })} className="inline-flex items-center gap-1 px-2.5 py-1 text-xs rounded-sm bg-primary text-primary-foreground hover:opacity-90" data-testid="btn-zentrale-antworten">
                      <Reply className="w-3.5 h-3.5" /> Antworten
                    </button>
                  )}
                  <button onClick={() => setSchreiben({ weiterleiten: true })} className="inline-flex items-center gap-1 px-2.5 py-1 text-xs rounded-sm border hover:bg-accent" data-testid="btn-zentrale-weiterleiten">
                    <Forward className="w-3.5 h-3.5" /> Weiterleiten
                  </button>
                </div>
              </div>
              {(fuehrung.hinweis || (portal && (imPortalGrund || mailAnKundenGrund))) && (
                <div className="text-xs rounded-sm px-2.5 py-2 border border-amber-300 bg-amber-50 text-amber-900 dark:bg-amber-950/40 dark:text-amber-100 space-y-0.5" data-testid="zentrale-fuehrung">
                  {fuehrung.hinweis && <div><Info className="w-3.5 h-3.5 inline mr-1 -mt-0.5" />{fuehrung.hinweis}</div>}
                  {portal && imPortalGrund && <div>„Im Portal antworten“: {imPortalGrund}</div>}
                  {portal && mailAnKundenGrund && <div>„Mail an Kunden“: {mailAnKundenGrund}</div>}
                </div>
              )}
              <div className="grid grid-cols-2 gap-2 text-xs">
                <div><div className="text-muted-foreground">Von</div><div className="break-all">{detail.from || detail.from_email || "—"}</div></div>
                <div><div className="text-muted-foreground">An</div><div className="break-all">{detail.to || "—"}</div></div>
                {detail.cc && <div><div className="text-muted-foreground">Cc</div><div className="break-all">{detail.cc}</div></div>}
                <div><div className="text-muted-foreground">Datum</div><div>{datumKurz(detail.date)}</div></div>
                <div><div className="text-muted-foreground">Postfach</div><div>{detail.account_label || "—"}</div></div>
              </div>

              {/* Zuordnung / Kundenblick */}
              <div className="border border-dashed rounded-sm p-2.5 space-y-2" data-testid="zentrale-kundenblick">
                <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Zuordnung</div>
                {detail.kunde ? (
                  <>
                    <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                      <span className="font-semibold" data-testid="zentrale-kunde-name">{detail.kunde.name}</span>
                      {(detail.kunde.phone || detail.kunde.mobile) && (
                        <a href={`tel:${(detail.kunde.phone || detail.kunde.mobile).replace(/\s/g, "")}`} className="inline-flex items-center gap-1 text-primary hover:underline text-xs">
                          <Phone className="w-3 h-3" /> {detail.kunde.phone || detail.kunde.mobile}
                        </a>
                      )}
                      {detail.kunde.address && <span className="text-xs text-muted-foreground">{detail.kunde.address}</span>}
                      <span className="text-[10px] text-muted-foreground">
                        {detail.kunde.quelle === "zuordnung" ? `(gemerkt: ${detail.kontakt_adresse})` : detail.kunde.quelle === "anfrage" ? "(über Formular-Anfrage)"
                          : detail.kunde.quelle === "portal" ? `(über Kundenportal: ${portal?.weg || "Portal"})`
                          : detail.kunde.gefunden_ueber ? `(über Mail-Adresse – gefunden über: ${detail.kunde.gefunden_ueber})` : "(über Mail-Adresse)"}
                      </span>
                    </div>
                    {portal?.beschreibung && (
                      <div className="text-xs" data-testid="zentrale-portal-projekt">
                        <span className="text-muted-foreground">Portal-Projekt: </span><b>{portal.beschreibung}</b>
                        {!portal.projekt_id && <span className="text-muted-foreground"> (kein gleichnamiges Projekt in der Suite)</span>}
                      </div>
                    )}
                    {detail.projekte.length > 0 ? (
                      <div className="flex flex-wrap gap-1.5" data-testid="zentrale-projekte">
                        {detail.projekte.map((p) => (
                          <button key={p.id} onClick={() => projektOeffnen(p)} className={`text-left border rounded-sm px-2 py-1 text-xs hover:bg-muted ${p.archiv ? "opacity-60" : ""} ${portal?.projekt_id === p.id ? "border-violet-400 bg-violet-50 dark:bg-violet-950/30" : ""}`} title={portal?.projekt_id === p.id ? "Projekt des Kundenportals – öffnen" : "Projekt öffnen"}>
                            <span className="font-medium">{p.titel || "(ohne Titel)"}</span>
                            <span className="text-muted-foreground"> · {p.status || "—"}{p.erstellt ? ` · ${p.erstellt}` : ""}</span>
                          </button>
                        ))}
                      </div>
                    ) : (
                      <div className="text-xs text-muted-foreground">Noch kein Projekt.</div>
                    )}
                    <div className="flex flex-wrap gap-1.5">
                      <button onClick={() => navigate(`/module/kunden?edit=${detail.kunde.id}`)} className="inline-flex items-center gap-1 px-2.5 py-1 text-xs border rounded-sm hover:bg-accent" data-testid="btn-zentrale-kunde-oeffnen">
                        <ExternalLink className="w-3.5 h-3.5" /> Kunde öffnen
                      </button>
                      {aktiveProjekte.length > 0 && (
                        <button onClick={() => projektOeffnen(aktiveProjekte[0])} className="inline-flex items-center gap-1 px-2.5 py-1 text-xs border rounded-sm hover:bg-accent" data-testid="btn-zentrale-projekt-oeffnen">
                          <FolderOpen className="w-3.5 h-3.5" /> Projekt öffnen{aktiveProjekte.length > 1 ? ` (${aktiveProjekte[0].titel || "neuestes"})` : ""}
                        </button>
                      )}
                      {detail.kontakt_adresse && (
                        <button onClick={() => setZuordnen((v) => !v)} className="inline-flex items-center gap-1 px-2.5 py-1 text-xs border rounded-sm hover:bg-accent" data-testid="btn-zentrale-anders-zuordnen">
                          <Users className="w-3.5 h-3.5" /> Anderem Kunden zuordnen
                        </button>
                      )}
                      {detail.zuordnung_gemerkt && (
                        <button onClick={() => zuordnungSpeichern(null)} disabled={busy} className="px-2.5 py-1 text-xs border rounded-sm hover:bg-accent text-muted-foreground">Zuordnung lösen</button>
                      )}
                    </div>
                  </>
                ) : (
                  <>
                    <div className="text-sm">
                      {detail.kontakt_adresse ? <>Absender <b>{detail.kontakt_adresse}</b> ist noch kein Kunde.</>
                        : portal ? <>Kunde{portal.name_im_betreff ? <> „<b>{portal.name_im_betreff}</b>“</> : ""} nicht in der Suite gefunden.</>
                          : "Keine Kundenadresse erkennbar."}
                    </div>
                    {detail.kontakt_adresse && fuehrung.anlegen !== false && (
                      <div className="flex flex-wrap gap-1.5">
                        <button onClick={anlegenStarten} className="inline-flex items-center gap-1 px-2.5 py-1 text-xs rounded-sm bg-primary text-primary-foreground hover:opacity-90" data-testid="btn-zentrale-als-kunde">
                          <UserPlus className="w-3.5 h-3.5" /> Als Kunde anlegen
                        </button>
                        <button onClick={() => setZuordnen((v) => !v)} className="inline-flex items-center gap-1 px-2.5 py-1 text-xs border rounded-sm hover:bg-accent" data-testid="btn-zentrale-zuordnen">
                          <Users className="w-3.5 h-3.5" /> Bestehendem Kunden zuordnen
                        </button>
                      </div>
                    )}
                  </>
                )}
                {adrFrage && adrFrage.adresse === detail.kontakt_adresse && (
                  <div className="space-y-2 rounded-sm border border-emerald-300 bg-emerald-50/60 p-2 dark:bg-emerald-950/30" data-testid="zentrale-adresse-frage">
                    <div className="text-xs">Adresse <b className="break-all">{adrFrage.adresse}</b> beim Kunden <b>{adrFrage.kunde_name}</b> speichern? Dann findet der Mailverlauf alle Mails dieser Adresse.</div>
                    <AuswahlFeld docType="email_rolle" label="Rolle" value={adrFrage.rolle} onChange={(v) => setAdrFrage((f) => ({ ...f, rolle: v }))}
                      ersatz={MAIL_ROLLEN_ERSATZ} leer="— Rolle wählen —" stichwoerter={false} testId="zentrale-adresse-rolle" />
                    <div className="flex gap-2">
                      <button onClick={adresseSpeichern} disabled={busy} className="px-2.5 py-1 text-xs rounded-sm bg-emerald-600 text-white disabled:opacity-50" data-testid="btn-zentrale-adresse-ja">Ja, speichern</button>
                      <button onClick={() => setAdrFrage(null)} className="px-2.5 py-1 text-xs border rounded-sm hover:bg-accent" data-testid="btn-zentrale-adresse-nein">Nein</button>
                    </div>
                  </div>
                )}
                {zuordnen && (
                  <div className="space-y-1" data-testid="zentrale-zuordnen-suche">
                    <div className="text-xs text-muted-foreground">Mails von {detail.kontakt_adresse} künftig diesem Kunden zuordnen:</div>
                    <MailKundenSuche onWaehle={zuordnungSpeichern} autoFocus testid="zentrale-zuordnen" />
                  </div>
                )}
              </div>

              <div>
                <div className="text-xs text-muted-foreground mb-1">Inhalt</div>
                <pre className="text-xs whitespace-pre-wrap bg-muted/40 rounded-sm p-3 border max-h-[40vh] overflow-auto break-words" data-testid="zentrale-text">
                  {detail.body || "(kein lesbarer Text)"}
                </pre>
              </div>

              {(detail.anhaenge > 0 || detail.google_drive_anzahl > 0) && (
                <MailAnhaengeBereich meta={{ account_id: detail.account_id, folder: detail.folder, uid: detail.uid }} email={detail.kunde?.email || ""} kunde={detail.kunde || null} />
              )}
            </div>
          )}
        </div>
      </div>

      {/* Antworten / Weiterleiten: Mail-Fenster aus M1 */}
      <MailSchreibenModal
        isOpen={!!schreiben && !!detail}
        onClose={() => setSchreiben(null)}
        kundeId={detail?.kunde?.id || ""}
        ohneKunde={!schreiben?.anKunden}
        weiterleiten={!!schreiben?.weiterleiten}
        titel={schreiben?.anKunden ? `Mail an ${detail?.kunde?.name || "Kunden"}` : ""}
        mail={detail && !schreiben?.anKunden ? { account_id: detail.account_id, folder: detail.folder, uid: detail.uid } : null}
        projekte={(detail?.projekte || []).map((p) => ({ id: p.id, titel: p.titel }))}
        projektId={startProjekt}
        onGesendet={() => richtung === "aus" && neuLaden(true)}
      />

      {/* Als Kunde anlegen (Kundenanlage /modules/kunden/data, Name/Mail vorausgefüllt) */}
      <Modal isOpen={!!anlegen} onClose={() => { setAnlegen(null); setDupl(null); }} title="Als Kunde anlegen" size="md">
        {anlegen && (
          <div className="space-y-2 text-sm" data-testid="zentrale-kunde-anlegen">
            <div className="grid grid-cols-2 gap-2">
              <label className="block"><span className="text-xs text-muted-foreground">Anrede</span>
                <select className={feld} value={anlegen.anrede} onChange={(e) => setAnlegen({ ...anlegen, anrede: e.target.value })}>
                  <option value="">—</option><option>Herr</option><option>Frau</option>
                </select>
              </label>
              <label className="block"><span className="text-xs text-muted-foreground">Firma</span>
                <input className={feld} value={anlegen.firma} onChange={(e) => setAnlegen({ ...anlegen, firma: e.target.value })} />
              </label>
              <label className="block"><span className="text-xs text-muted-foreground">Vorname</span>
                <input className={feld} value={anlegen.vorname} onChange={(e) => setAnlegen({ ...anlegen, vorname: e.target.value })} data-testid="anlegen-vorname" />
              </label>
              <label className="block"><span className="text-xs text-muted-foreground">Nachname</span>
                <input className={feld} value={anlegen.nachname} onChange={(e) => setAnlegen({ ...anlegen, nachname: e.target.value })} data-testid="anlegen-nachname" />
              </label>
              <label className="block"><span className="text-xs text-muted-foreground">E-Mail</span>
                <input className={feld} value={anlegen.email} onChange={(e) => setAnlegen({ ...anlegen, email: e.target.value })} data-testid="anlegen-email" />
              </label>
              <label className="block"><span className="text-xs text-muted-foreground">Telefon</span>
                <input className={feld} value={anlegen.phone} onChange={(e) => setAnlegen({ ...anlegen, phone: e.target.value })} />
              </label>
            </div>
            <div className="text-xs text-muted-foreground">Adresse und weitere Angaben danach in der Kundenkarte („Kunde öffnen“).</div>
            {dupl && (
              <div className="border border-amber-300 bg-amber-50 dark:bg-amber-950/30 rounded-sm p-2 space-y-1.5 text-xs" data-testid="anlegen-duplikate">
                <div className="font-medium">Diesen Kunden gibt es vielleicht schon:</div>
                {dupl.map((d) => (
                  <div key={d.id} className="flex items-center justify-between gap-2">
                    <span className="truncate">{d.name} · {d.email || "—"} · {d.phone || "—"}</span>
                    <button onClick={() => { setAnlegen(null); setDupl(null); zuordnungSpeichern({ id: d.id }); }} className="px-2 py-0.5 border rounded-sm hover:bg-accent shrink-0">Diesem zuordnen</button>
                  </div>
                ))}
                <button onClick={() => anlegenSenden(true)} disabled={busy} className="px-2 py-0.5 border rounded-sm hover:bg-accent">Trotzdem neu anlegen</button>
              </div>
            )}
            <div className="flex justify-end gap-2 pt-1">
              <button onClick={() => { setAnlegen(null); setDupl(null); }} className="px-3 py-1.5 text-sm border rounded-sm hover:bg-accent">Abbrechen</button>
              <button onClick={() => anlegenSenden(false)} disabled={busy || !(anlegen.nachname || anlegen.firma || anlegen.vorname)}
                className="px-3 py-1.5 text-sm bg-primary text-primary-foreground rounded-sm hover:opacity-90 disabled:opacity-50" data-testid="btn-anlegen-speichern">
                {busy ? "Speichere…" : "Kunde anlegen"}
              </button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default MailZentraleListe;
